// Copyright (c) 2022-2024. Sendanor <info@sendanor.fi>. All rights reserved.

import { explainEnum, isEnum, parseEnum, stringifyEnum } from "../../../../io/hyperify/core/types/Enum";
import { explainNot, explainOk, explainOr } from "../../../../io/hyperify/core/types/explain";
import { isUndefined } from "../../../../io/hyperify/core/types/undefined";

export enum SendanorProductId {

    VPS_MINI_1 = "vps-mini-1",
    VPS_1 = "vps-1",
    VPS_2 = "vps-2",
    VPS_3 = "vps-3",
    VPS_4 = "vps-4",
    VPS_5 = "vps-5",
    VPS_6 = "vps-6",
    VPS_7 = "vps-7",
    VPS_8 = "vps-8",

    VPS_10 = "vps-10",
    VPS_11 = "vps-11",
    VPS_12 = "vps-12",
    VPS_13 = "vps-13",
    VPS_14 = "vps-14",

    VPS_CPU_1_10 = "vps-cpu-1-10",
    VPS_CPU_1_5 = "vps-cpu-1-5",
    VPS_MEMORY_MINI_1 = "vps-mini-mem-1",
    VPS_MEMORY_1 = "vps-memory-1",
    VPS_NET6_1 = "vps-net6-1",
    VPS_IP4_1 = "vps-ip4-1",
    VPS_ETH2G_1 = "vps-eth2g-1",
    VPS_ETH5G_1 = "vps-eth5g-1",
    VPS_ETH10G_1 = "vps-eth10g-1",
    VPS_SAS_250 = "vps-sas-250",
    VPS_SAS_500 = "vps-sas-500",
    VPS_SAS_1000 = "vps-sas-1000",
    VPS_NVME_10 = "vps-nvme-10",
    VPS_SUPPORT_1 = "vps-support-1",
    VPS_UPGRADE_1 = "vps-upgrade-1",
    VPS_BACKUP_RESTORE_1 = "vps-backup-restore-1",

    WEB_HOTEL_DISK_5 = "web-disk-5",
    WEB_HOTEL_DISK_25 = "web-disk-25",
    WEB_HOTEL_DISK_50 = "web-disk-50",
    WEB_HOTEL_BACKUP_RESTORE_1 = "web-backup-restore-1",
    WEB_MYSQL_1 = "web-mysql-1",
    WEB_PSQL_1 = "web-psql-1",
    WEB_SUPPORT_1 = "web-support-1",
    WEB_USER_1 = "web-user-1",

    ATLAS_SUPPORT_1 = "atlas-support-1",
    ATLAS_USER_1 = "atlas-user-1",

    EMAIL_DISK_10 = "email-disk-10",
    EMAIL_DISK_50 = "email-disk-50",
    EMAIL_BACKUP_RESTORE_1 = "email-backup-restore-1",
    EMAIL_SUPPORT_1 = "email-support-1",

    /**
     * @deprecated Use WEB_TITAN_5G
     */
    WEB_1 = "web-1",

    /**
     * @deprecated Use WEB_ATLAS_5G
     */
    WEB_2 = "web-2",

    WEB_TITAN_1G  = "web-titan-1g",
    WEB_TITAN_2G  = "web-titan-2g",
    WEB_TITAN_5G  = "web-titan-5g",
    WEB_TITAN_10G = "web-titan-10g",
    WEB_TITAN_20G = "web-titan-20g",

    WEB_ATLAS_1G  = "web-atlas-1g",
    WEB_ATLAS_2G  = "web-atlas-2g",
    WEB_ATLAS_5G  = "web-atlas-5g",
    WEB_ATLAS_10G = "web-atlas-10g",
    WEB_ATLAS_20G = "web-atlas-20g",

    ATLAS_18G = "atlas-18g",

    WP_1 = "wp-1",
    NET_1 = "net-1",
    WEBRTC_1 = "webrtc-1",

    EMAIL_MIMI = "email-mini",
    EMAIL_1 = "email-1",
    EMAIL_2 = "email-2",
    EMAIL_3 = "email-3",

    SHELL_1 = "shell-1",
    SHELL_2 = "shell-2",

    DOMAIN = "domain",
    STOCK = "stock",
    OUTLET = "outlet",

    SOFTWARE_GOVM_BIN_1 = "govm-bin-1",
    SOFTWARE_GOVM_FSL_2 = "govm-fsl-2",

}

export const ALL_VPS_EXTRA_PRODUCT_IDS = [
    SendanorProductId.VPS_CPU_1_10,
    // SendanorProductId.VPS_CPU_1_5, // Not in store now
    SendanorProductId.VPS_MEMORY_MINI_1,
    SendanorProductId.VPS_MEMORY_1,
    SendanorProductId.VPS_NET6_1,
    SendanorProductId.VPS_IP4_1,
    SendanorProductId.VPS_ETH2G_1,
    SendanorProductId.VPS_ETH5G_1,
    SendanorProductId.VPS_ETH10G_1,
    SendanorProductId.VPS_SAS_250,
    SendanorProductId.VPS_SAS_500,
    SendanorProductId.VPS_SAS_1000,
    SendanorProductId.VPS_NVME_10,
    SendanorProductId.VPS_SUPPORT_1,
    SendanorProductId.VPS_UPGRADE_1,
    SendanorProductId.VPS_BACKUP_RESTORE_1,
];

export const ALL_WEB_EXTRA_PRODUCT_IDS = [
    SendanorProductId.WEB_SUPPORT_1,
    SendanorProductId.WEB_USER_1,
    SendanorProductId.WEB_MYSQL_1,
    SendanorProductId.WEB_PSQL_1,
    SendanorProductId.WEB_HOTEL_BACKUP_RESTORE_1,
    SendanorProductId.WEB_HOTEL_DISK_5,
    SendanorProductId.WEB_HOTEL_DISK_25,
    SendanorProductId.WEB_HOTEL_DISK_50,
];

export const ALL_EMAIL_EXTRA_PRODUCT_IDS = [
    SendanorProductId.EMAIL_DISK_10,
    SendanorProductId.EMAIL_DISK_50,
    SendanorProductId.EMAIL_BACKUP_RESTORE_1,
    SendanorProductId.EMAIL_SUPPORT_1,
];

export const ALL_ATLAS_EXTRA_PRODUCT_IDS = [
    SendanorProductId.ATLAS_USER_1,
    SendanorProductId.ATLAS_SUPPORT_1,
];

export function isSendanorProductId (value: unknown) : value is SendanorProductId {
    return isEnum(SendanorProductId, value);
}

export function explainSendanorProductId (value : unknown) : string {
    return explainEnum("SendanorProductId", SendanorProductId, isSendanorProductId, value);
}

export function stringifySendanorProductId (value : SendanorProductId) : string {
    return stringifyEnum(SendanorProductId, value);
}

export function parseSendanorProductId (value: any) : SendanorProductId | undefined {
    return parseEnum(SendanorProductId, value) as SendanorProductId | undefined;
}

export function isSendanorProductIdOrUndefined (value: unknown): value is SendanorProductId | undefined {
    return isUndefined(value) || isSendanorProductId(value);
}

export function explainSendanorProductIdOrUndefined (value: unknown): string {
    return isSendanorProductIdOrUndefined(value) ? explainOk() : explainNot(explainOr(['SendanorProductId', 'undefined']));
}

export function getSendanorProductIdForDomain (domain: string) : string {
    return `${SendanorProductId.DOMAIN}#${domain}`;
}

export function getSendanorProductIdForOutlet (id: string) : string {
    return `${SendanorProductId.OUTLET}#${id}`;
}
